import { ResyncBlueIcon } from '@assets/icons/index';
import { Box, Divider, Flex, Grid, IconButton, ListItem, Spinner, Text, UnorderedList } from '@chakra-ui/react';
import InputComp from '@components/formField/InputComp';
import { isEmpty } from 'lodash';
import { DatePicker2 } from '@components/datePicker2';
import { MODE_CONSTRAINTS } from '@components/datePicker2/const';
import { useEffect, useMemo, useState } from 'react';
import { useSnackbarNotification } from '@store/snackbarNotification';
import { useGetInfiniteListMasterProduct } from '@pages/UploadMappingV2/hooks/query/useGetListMasterProduct';
import { useUserInfo } from '@store/userInfo';
import { ConditionalComponent } from '@components/functionComponent';
import { dateFormatter } from '@helpers/utils';
import { useSkuUpsertModal } from '@pages/UploadSellout/hooks/state/useSkuUpsertModal';
import moment from 'moment';
import { defineInputType } from '@pages/UploadSellout/helpers/utils';

const MOCK_PRODUCT_DATABASE = {
    12345: {
        is_bundle: false,
        name: '[MOCK] Wardah Banded B1G1 Perfect Bright Micellar Water 100ml',
    },
    67890: {
        is_bundle: true,
        bundle_name: '[MOCK] Perfect Bright Glow Bundle Set',
        bundle_products: [
            { id: 1, name: 'Wardah Banded B1G1 Perfect Bright Micellar Water 100ml' },
            { id: 1, name: 'Wardah Banded B1G1 Perfect Bright Micellar Water 100ml' },
            { id: 3, name: 'Wardah Banded B1G1 Tidak Bright Micellar Water 100ml' },
            { id: 4, name: 'Wardah Banded B1G1 Hampir Bright Micellar Water 100ml' },
        ],
    },
};

const SectionBody = ({ localForm, setLocalForm, setIsSynced }) => {
    const { onChangeSnackbarNotification } = useSnackbarNotification();
    const { userInfo } = useUserInfo();
    const [query, setQuery] = useState(null);
    const [isTriggered, setIsTriggered] = useState(false);
    const [isMockLoading, setIsMockLoading] = useState(false);
    const [mockProductData, setMockProductData] = useState(null);
    const { mode, form } = useSkuUpsertModal();

    const selectedTimeUploadType = JSON.parse(localStorage.getItem('selectedTimeUploadType'));
    const currentSkuInput = localForm.code?.trim() || localForm.barcode?.trim();
    const isMockMode = !!MOCK_PRODUCT_DATABASE[currentSkuInput];
    const today = new Date();
    const maxMonth = new Date(today.getFullYear(), today.getMonth(), 0);

    const {
        data,
        isLoading: isApiLoading,
        isSuccess: isApiSuccess,
        isError: isApiError,
        error,
    } = useGetInfiniteListMasterProduct({
        ...query,
        enabled: (!!query?.search || !!query?.customer_id) && !isMockMode,
    });

    const gridTemplateArea = `
        "barcode           ."
        "productInfo       productInfo"
        "date              qty"
        "customer_subtotal customer_final_subtotal"
    `;

    const parsedProduct = useMemo(() => {
        if (isMockMode) return mockProductData;
        if (!data?.length) return null;

        setIsSynced(true);
        return data[0] || null;
    }, [data, isMockMode, mockProductData]);

    const isComponentLoading = isMockMode ? isMockLoading : isApiLoading;
    const isSearchSuccess = isMockMode ? !isMockLoading && isTriggered : isApiSuccess;
    const isProductFound = isSearchSuccess && !!parsedProduct;
    const isBundle = !!parsedProduct?.product_list?.length;

    const groupedProducts = useMemo(() => {
        const productList = parsedProduct?.product_list || [];
        if (!isBundle || isEmpty(productList)) return [];

        return Object.values(
            productList.reduce((acc, item) => {
                const name = item.name || item.product_name;
                acc[name] ??= { name, qty: 0 };
                acc[name].qty += item.qty || 1;
                return acc;
            }, {}),
        );
    }, [parsedProduct, isBundle]);

    const handleFormChange = (key, value) => {
        setLocalForm((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleResync = () => {
        const skuInput = localForm.code?.trim() || localForm.barcode?.trim();

        if (!skuInput) {
            onChangeSnackbarNotification({
                status: 'error',
                description: 'Silahkan masukkan nomor barcode atau SKU terlebih dahulu.',
                duration: 3,
            });
            return;
        }

        setIsTriggered(true);

        if (isMockMode) {
            setIsMockLoading(true);
            setMockProductData(null);
            setTimeout(() => {
                setMockProductData(MOCK_PRODUCT_DATABASE[skuInput]);
                setIsMockLoading(false);
            }, 500);
        } else {
            const params = {
                search: localForm.code || localForm.barcode,
                customer_id: userInfo.customer_id,
            };
            setQuery({
                search: params,
            });
        }
    };

    useEffect(() => {
        if (!isTriggered) return;

        if (isSearchSuccess && parsedProduct) {
            const productCode = parsedProduct?.code;

            setLocalForm((prev) => ({
                ...prev,
                name: parsedProduct.name,
                ...(isBundle && { product_list: parsedProduct.product_list }),
                ...(!!productCode && { barcode: '', code: productCode }),
            }));
        }

        if (isSearchSuccess && !parsedProduct) {
            setLocalForm((prev) => ({ ...prev, name: undefined, code: '' }));
        }
    }, [isSearchSuccess, parsedProduct, isTriggered]);

    useEffect(() => {
        if (!isMockMode && isApiError && error?.response?.status === 404 && isTriggered) {
            setLocalForm((prev) => ({ ...prev, name: undefined }));
            onChangeSnackbarNotification({
                status: 'error',
                description: 'Data produk tidak ditemukan.',
                duration: 3,
            });
        }
        if (!isMockMode && isApiError && error?.response?.status === 500 && isTriggered) {
            setLocalForm((prev) => ({ ...prev, name: undefined }));
            onChangeSnackbarNotification({
                status: 'error',
                description: 'Terjadi kesalahan sistem saat mengambil data produk.',
                duration: 3,
            });
        }
    }, [isApiError, isTriggered, isMockMode]);

    return (
        <Grid
            templateAreas={gridTemplateArea}
            rowGap="32px"
            columnGap="24px"
            mb={'32px'}
            gridTemplateColumns={'repeat(2, 1fr)'}
        >
            <InputComp
                label="Customer SKU/Barcode"
                placeholder="Tuliskan customer SKU code / barcode"
                isRequired={true}
                isDimBg={!isEmpty(localForm.code || localForm.barcode)}
                value={localForm.code || localForm.barcode}
                onChange={(value) => {
                    if (!value) {
                        setIsTriggered(false);
                        setMockProductData(null);
                        setLocalForm((prev) => ({ ...prev, name: undefined }));
                    }

                    if (defineInputType(value) === 'code') {
                        setLocalForm((prev) => ({ ...prev, barcode: '' }));
                    } else {
                        setLocalForm((prev) => ({ ...prev, code: '' }));
                    }

                    handleFormChange(defineInputType(value), value);
                    setIsSynced(false);
                }}
                extraStyle={{ gridArea: 'barcode' }}
                debounceDelay={0}
            />

            <Box gridArea={'productInfo'}>
                <Divider mb={'28px'} />

                <Flex justifyContent={'space-between'} alignItems={'center'} mb={'22px'}>
                    <Text fontWeight={700} fontSize={'14px'}>
                        Informasi Produk
                    </Text>
                    <IconButton
                        size={'sm'}
                        variant={'outline'}
                        icon={isComponentLoading ? <Spinner size="xs" /> : <ResyncBlueIcon />}
                        onClick={handleResync}
                        isDisabled={isComponentLoading}
                    />
                </Flex>

                <ConditionalComponent
                    statement={isProductFound || (mode === 'edit' && localForm.name)}
                    fallback={
                        isTriggered && isSearchSuccess && !parsedProduct ? (
                            <Text fontSize={'14px'} color={'red.500'} fontWeight="500">
                                Tidak ada produk yang sesuai atau produk belum melalui proses mapping
                            </Text>
                        ) : !localForm.name ? (
                            <Text fontSize={'14px'} color={'red.500'} fontWeight="500">
                                Tidak ada produk yang sesuai atau produk belum melalui proses mapping
                            </Text>
                        ) : (
                            <Text fontSize={'14px'} color={'semantic.400'}>
                                Tuliskan nomor barcode atau customer SKU terlebih dahulu.
                            </Text>
                        )
                    }
                >
                    <ConditionalComponent statement={isBundle}>
                        <Flex gap={'42px'} mb={'16px'}>
                            <Text fontWeight={700} fontSize={'14px'}>
                                Nama Bundle
                            </Text>
                            <Text fontSize={'14px'}>{parsedProduct?.bundle_name || parsedProduct?.name}</Text>
                        </Flex>
                    </ConditionalComponent>

                    <Grid templateColumns="110px 1fr" gap={1}>
                        <Text fontWeight={700} fontSize={'14px'}>
                            Produk Paragon
                        </Text>
                        <ConditionalComponent
                            statement={isBundle}
                            fallback={
                                <Text ms={'14px'} fontSize={'14px'}>
                                    {mode === 'edit' ? localForm.name : parsedProduct?.name}
                                </Text>
                            }
                        >
                            <UnorderedList fontSize={'14px'} styleType="none" spacing={'8px'}>
                                {groupedProducts.map((item) => (
                                    <ListItem key={item.name} display={'flex'} flex={1}>
                                        <Text>{item.name}</Text>
                                        <Text>{item.qty}x</Text>
                                    </ListItem>
                                ))}
                            </UnorderedList>
                        </ConditionalComponent>
                    </Grid>
                </ConditionalComponent>
                <Divider mt={'28px'} />
            </Box>

            <DatePicker2
                isDimBg={localForm.date}
                mode={MODE_CONSTRAINTS.SINGLE}
                onlyMonthPicker={selectedTimeUploadType === 'monthly'}
                maxDate={maxMonth}
                label="Sellout Date"
                placeholder="Masukkan Sellout Date"
                onChange={(value) => {
                    const [selectedDate] = value;
                    const initializeDate = new Date(selectedDate);
                    const firstDate = moment(initializeDate).startOf('month');
                    handleFormChange('date', selectedTimeUploadType === 'daily' ? initializeDate : firstDate);
                }}
                formatDisplayValue={() =>
                    dateFormatter(
                        new Date(localForm.date),
                        selectedTimeUploadType === 'monthly' ? 'MMM YYYY' : 'DD MMM YYYY',
                    )
                }
                extraStyle={{ gridArea: 'date' }}
                isRequired
                isDisableSelectToday
            />

            <InputComp
                label="Quantity"
                placeholder="Masukkan quantity"
                isRequired={true}
                isDimBg={localForm.qty}
                value={localForm.qty}
                onChange={(value) => {
                    handleFormChange('qty', value);
                }}
                extraStyle={{ gridArea: 'qty' }}
                type="number"
                debounceDelay={0}
            />

            <InputComp
                label="Subtotal"
                placeholder="Masukkan subtotal value"
                isDimBg={localForm.customer_subtotal}
                value={localForm.customer_subtotal ?? ''}
                onChange={(value) => {
                    handleFormChange('customer_subtotal', value);
                }}
                extraStyle={{ gridArea: 'customer_subtotal' }}
                isRupiah
                debounceDelay={0}
            />

            <InputComp
                label="Final Subtotal"
                placeholder="Masukkan final subtotal value"
                isDimBg={localForm.customer_final_subtotal}
                value={localForm.customer_final_subtotal ?? ''}
                onChange={(value) => {
                    handleFormChange('customer_final_subtotal', value);
                }}
                extraStyle={{ gridArea: 'customer_final_subtotal' }}
                isRupiah
                debounceDelay={0}
            />
        </Grid>
    );
};

export default SectionBody;
